import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ArrowRight,
  Bot,
  CalendarDays,
  Database,
  FolderPlus,
  List,
  MessageCircle,
  Radio,
  Search,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import BackBtn from "../widgets/BackBtn";

const PAGE_SIZE = 15;

const formatDate = (dateString) => {
  if (!dateString) return "Not available";

  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ChannelMetric = ({ icon: Icon, label, value, color, background }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.25,
      py: 1,
      borderRadius: 2,
      bgcolor: background,
    }}
  >
    <Icon size={17} color={color} strokeWidth={2} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 15, lineHeight: 1, fontWeight: 800, color }}>
        {Number(value || 0).toLocaleString("en-IN")}
      </Typography>
      <Typography
        sx={{
          mt: 0.4,
          fontSize: 10.5,
          lineHeight: 1.2,
          color: "text.secondary",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

const CampaignGroupCard = ({ group, onOpen }) => {
  const whatsappCount = group.whatsappCampaignCount ?? 0;
  const rcsCount = group.rcsCampaignCount ?? 0;
  const totalCampaigns = group.campaignCount ?? whatsappCount + rcsCount;

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: 2.5,
        border: "1px solid #e4e8f0",
        borderRadius: 3,
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        transition: "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, #7c3aed, #2563eb, #0d9488)",
        },
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: "#c8cfee",
          boxShadow: "0 14px 32px rgba(76, 67, 150, 0.10)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: 21,
              lineHeight: 1.25,
              fontWeight: 800,
              letterSpacing: "-0.015em",
              color: "#172033",
              overflowWrap: "anywhere",
            }}
          >
            {group.name || "Untitled campaign group"}
          </Typography>
          <Box
            sx={{
              mt: 0.9,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: "text.secondary",
            }}
          >
            <Users size={15} strokeWidth={1.8} />
            <Typography
              sx={{
                fontSize: 12.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {group.contactList || "No audience connected"}
            </Typography>
          </Box>
        </Box>
        <Chip
          size="small"
          icon={
            group.isDb ? (
              <Database size={13} strokeWidth={2} />
            ) : (
              <List size={13} strokeWidth={2} />
            )
          }
          label={group.isDb ? "Database audience" : "Contact-list audience"}
          sx={{
            height: 25,
            bgcolor: group.isDb ? "#fff7ed" : "#eff6ff",
            color: group.isDb ? "#b45309" : "#1d4ed8",
            border: "1px solid",
            borderColor: group.isDb ? "#fed7aa" : "#bfdbfe",
            fontSize: 10.5,
            fontWeight: 700,
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
        <ChannelMetric
          icon={MessageCircle}
          label="WhatsApp"
          value={whatsappCount}
          color="#087a55"
          background="#eaf8f2"
        />
        <ChannelMetric
          icon={Radio}
          label="RCS"
          value={rcsCount}
          color="#6d4bd1"
          background="#f2effc"
        />
      </Stack>

      <Box
        sx={{
          mt: 2.25,
          pt: 1.75,
          borderTop: "1px solid #edf0f4",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.6}>
            <CalendarDays size={13} color="#94a3b8" strokeWidth={1.8} />
            <Typography sx={{ fontSize: 10.5, color: "text.secondary" }}>
              Created {formatDate(group.createdOn)}
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.4, fontSize: 11, fontWeight: 700, color: "#475569" }}>
            {totalCampaigns.toLocaleString("en-IN")} campaign
            {totalCampaigns === 1 ? "" : "s"}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="text"
          endIcon={<ArrowRight size={15} />}
          onClick={onOpen}
          sx={{
            flexShrink: 0,
            color: "#5b4bb7",
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
            "&:hover": { bgcolor: "#f2effc" },
          }}
        >
          Open
        </Button>
      </Box>
    </Paper>
  );
};

const UnifiedCampaignsList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [audienceType, setAudienceType] = useState("all");

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);

    return () => clearTimeout(debounceTimer);
  }, [searchInput]);

  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });

  if (search) queryParams.set("search", search);
  if (audienceType !== "all") queryParams.set("audienceType", audienceType);

  const { data, isLoading, isError } = useApiEp(
    `/campaign/campaign-groups?${queryParams.toString()}`
  );

  const groups = data?.data ?? [];
  const currentPage = data?.currentPage ?? page;
  const totalPages = data?.totalPages ?? 1;
  const totalItems = data?.totalItems ?? 0;
  const firstItem = totalItems > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const lastItem = Math.min(currentPage * PAGE_SIZE, totalItems);
  const hasFilters = Boolean(search || audienceType !== "all");

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setAudienceType("all");
    setPage(1);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f7f9fc", py: 3 }}>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <BackBtn />

        <Paper
          elevation={0}
          sx={{
            mt: 2,
            mb: 3,
            p: { xs: 2.25, sm: 3 },
            border: "1px solid #e1e5f0",
            borderRadius: 3.5,
            background:
              "linear-gradient(135deg, #faf8ff 0%, #f2f6ff 58%, #effcf9 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 220,
              height: 220,
              borderRadius: "50%",
              bgcolor: "rgba(124, 58, 237, 0.06)",
              top: -135,
              right: -45,
            }}
          />
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={1.75} alignItems="center">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2.5,
                  background: "linear-gradient(135deg, #7c3aed, #4f46e5)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  boxShadow: "0 10px 24px rgba(109, 75, 209, 0.24)",
                  flexShrink: 0,
                }}
              >
                <Sparkles size={25} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 24, sm: 28 },
                    lineHeight: 1.15,
                    fontWeight: 800,
                    letterSpacing: "-0.025em",
                    color: "#172033",
                  }}
                >
                  AI Campaigns List
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                  Organize audiences and orchestrate WhatsApp and RCS campaigns
                  from intelligent workspaces.
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              startIcon={<FolderPlus size={18} />}
              onClick={() => navigate("/home/add-campaign-group")}
              sx={{
                flexShrink: 0,
                borderRadius: 2.25,
                px: 2,
                py: 1.1,
                bgcolor: "#6350c7",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(99, 80, 199, 0.22)",
                "&:hover": { bgcolor: "#5543b2" },
              }}
            >
              New campaign group
            </Button>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
            p: 1.5,
            mb: 1.75,
            border: "1px solid #e4e8f0",
            borderRadius: 2.75,
            bgcolor: "#fff",
          }}
        >
          <TextField
            size="small"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search campaign group by name"
            inputProps={{ "aria-label": "Search campaign groups by name" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#64748b" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear campaign search"
                    onClick={() => setSearchInput("")}
                  >
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: "100%", md: 360 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "#f8fafc",
              },
            }}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={audienceType}
            onChange={(_, nextType) => {
              if (!nextType) return;
              setAudienceType(nextType);
              setPage(1);
            }}
            aria-label="Filter by audience source"
            sx={{
              alignSelf: { xs: "stretch", md: "center" },
              "& .MuiToggleButton-root": {
                flex: { xs: 1, md: "initial" },
                px: { xs: 1, sm: 1.75 },
                py: 0.75,
                borderColor: "#dde2eb",
                color: "#64748b",
                fontSize: 11.5,
                fontWeight: 700,
                textTransform: "none",
                gap: 0.65,
                "&.Mui-selected": {
                  color: "#5b4bb7",
                  bgcolor: "#f1effb",
                  borderColor: "#cfc8ef",
                  "&:hover": { bgcolor: "#eae6f8" },
                },
              },
            }}
          >
            <ToggleButton value="all">All audiences</ToggleButton>
            <ToggleButton value="database">
              <Database size={14} /> Database
            </ToggleButton>
            <ToggleButton value="contact">
              <Users size={14} /> Contact list
            </ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.75,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 750, color: "#1e293b" }}>
              Campaign workspaces
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
              {totalItems.toLocaleString("en-IN")} workspace
              {totalItems === 1 ? "" : "s"} {hasFilters ? "found" : "available"}
            </Typography>
          </Box>
          {totalItems > 0 && (
            <Chip
              size="small"
              label={`Showing ${firstItem}–${lastItem}`}
              sx={{ bgcolor: "#eef2ff", color: "#5b4bb7", fontWeight: 700 }}
            />
          )}
        </Box>

        {isLoading ? (
          <Paper
            elevation={0}
            sx={{ py: 8, border: "1px solid #e4e8f0", borderRadius: 3 }}
          >
            <MuPb />
          </Paper>
        ) : isError ? (
          <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
            AI campaign workspaces could not be loaded right now. Please try
            again shortly.
          </Alert>
        ) : groups.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              textAlign: "center",
              py: { xs: 7, sm: 9 },
              px: 2,
              border: "1.5px dashed #cfd5e2",
              borderRadius: 3,
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                width: 58,
                height: 58,
                mx: "auto",
                mb: 1.75,
                borderRadius: 3,
                background: "linear-gradient(135deg, #ede9fe, #e0e7ff)",
                color: "#6d4bd1",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Bot size={28} />
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 750, color: "#1e293b" }}>
              {hasFilters
                ? "No campaign workspaces match your search"
                : "Create your first AI campaign workspace"}
            </Typography>
            <Typography
              sx={{
                maxWidth: 430,
                mx: "auto",
                mt: 0.75,
                fontSize: 13,
                lineHeight: 1.55,
                color: "text.secondary",
              }}
            >
              {hasFilters
                ? "Try a different name or audience source to find the campaign group you need."
                : "Connect an audience once, then coordinate campaigns across your messaging channels from one place."}
            </Typography>
            <Button
              variant="outlined"
              startIcon={hasFilters ? <X size={17} /> : <FolderPlus size={17} />}
              onClick={
                hasFilters
                  ? clearFilters
                  : () => navigate("/home/add-campaign-group")
              }
              sx={{ mt: 2.25, borderRadius: 2, textTransform: "none", fontWeight: 700 }}
            >
              {hasFilters ? "Clear filters" : "Create campaign group"}
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {groups.map((group) => (
              <Grid item xs={12} md={6} key={group._id}>
                <CampaignGroupCard
                  group={group}
                  onOpen={() =>
                    navigate(`/home/${group._id}/campaign-group-detail`)
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}

        {!isLoading && !isError && totalPages > 1 && (
          <Paper
            elevation={0}
            sx={{
              mt: 3,
              px: 2,
              py: 1.5,
              border: "1px solid #e4e8f0",
              borderRadius: 2.5,
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Page {currentPage} of {totalPages} · {totalItems.toLocaleString("en-IN")} total
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, nextPage) => setPage(nextPage)}
              color="primary"
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
              sx={{
                "& .Mui-selected": {
                  bgcolor: "#6350c7 !important",
                  color: "#fff",
                },
              }}
            />
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default UnifiedCampaignsList;
